/**
 * 认证相关类型定义
 */

/**
 * 用户认证信息
 * 认证用户对象
 */
export interface AuthUser {
  id: string;
  uid: string;
  email?: string;
  phone?: string;
  nickname?: string;
  isAdmin?: boolean;
  avatarUrl?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  createdAt?: string;
  lastSignInAt?: string;
}

/**
 * 认证状态
 */
export type AuthState = {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
};

/**
 * 注册表单数据
 */
export interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  verificationCode: string;
  nickname: string;
}

/**
 * 登录表单数据
 */
export interface LoginFormData {
  email: string;
  password: string;
}

/**
 * 忘记密码表单数据
 */
export interface ForgotPasswordFormData {
  email: string;
  verificationCode: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * 营养数据
 */
export interface NutritionData {
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
}

export interface MealItem {
  id: string;
  name: string;
  calories: number;
  completed: boolean;
  icon: string;
  iconColorClass: string;
  recipeId?: string; // Optional link to a recipe
  completedDate?: string; // When the meal was marked complete
  nutrition?: NutritionData; // Nutrition data from linked recipe
  // 新增字段 - 计划管理功能扩展
  quantity?: number; // 份量倍数（默认1）
  note?: string; // 备注
  ingredients?: { name: string; amount: string }[]; // 缓存的食材信息
  recipeImage?: string; // 食谱图片URL缓存
  cookTime?: string; // 烹饪时间缓存
}

export interface MealTime {
  id?: string;
  time?: string;
  title: string;
  icon?: string;
  status?: '已完成' | '进行中' | '未开始';
  statusColorClass?: string;
  bgClass?: string;
  borderClass?: string;
  titleColorClass?: string;
  items: MealItem[];
}

export interface NutritionStat {
  label: string;
  current: number;
  max: number;
  unit: string;
  colorClass: string;
}

export interface Ingredient {
  name: string;
  icon: string;
  current: number;
  max: number;
  unit: string;
  colorClass: string;
}

export interface StockBatch {
  id: string;
  date: string; // Purchase date (YYYY-MM-DD)
  expirationDate: string; // Expiry date (YYYY-MM-DD)
  quantity: number;
  unit: string;
  location: string;
  daysLeft: number;
  note?: string;
}

/**
 * Inventory item for a specific ingredient in user's inventory
 */
export interface InventoryItem {
  currentAmount: number; // Current stock amount
  requiredAmount: number; // Required amount for shopping list
  unit: string; // 'g' or 'ml'
  batches: StockBatch[];
}

/**
 * User inventory mapping ingredient IDs to inventory items
 */
export type UserInventory = {
  [ingredientId: string]: InventoryItem;
};

/**
 * Base ingredient data (public or user-defined)
 * Does NOT include inventory data - that's stored separately in UserInventory
 */
export interface IngredientDetail {
    id: string;
    name: string;
    aliasNames?: string[];
    englishName?: string;
    category: string;
    subCategory: string;
    calories: number; // per 100g
    image: string;
    description: string;
    unit: string; // 'g' or 'ml'
    nutrition: {
      protein: number;
      fat: number;
      carbs: number;
    fiber: number;
  };

  // 扩展营养信息(可选)
  nutritionExtended?: {
    water?: number;        // 水分(g)
    energy?: number;       // 能量(kJ)
    cholesterol?: number;  // 胆固醇(mg)
    ash?: number;          // 灰分(g)
    carotene?: number;     // 胡萝卜素(μg)
    vitaminA?: number;     // 维生素A(μg)
    alphaTE?: number;      // α-TE(mg)
    thiamin?: number;      // 硫胺素(mg)
    riboflavin?: number;   // 核黄素(mg)
    niacin?: number;       // 烟酸(mg)
    vitaminC?: number;     // 维生素C(mg)
    calcium?: number;      // 钙(mg)
    phosphorus?: number;   // 磷(mg)
    potassium?: number;    // 钾(mg)
    sodium?: number;       // 钠(mg)
    magnesium?: number;    // 镁(mg)
    iron?: number;         // 铁(mg)
    zinc?: number;         // 锌(mg)
    selenium?: number;     // 硒(μg)
    copper?: number;       // 铜(mg)
    manganese?: number;    // 锰(mg)
    iodine?: number;       // 碘(μg)
    fattyAcidSaturated?: number;       // 饱和脂肪酸(g)
    fattyAcidMonounsaturated?: number; // 单不饱和脂肪酸(g)
    fattyAcidPolyunsaturated?: number; // 多不饱和脂肪酸(g)
  };

  // 可食部分百分比(可选)
  ediblePortion?: number;  // 0-100

  // 新增字段
  ingredient_id?: string; // 外部/业务ID
  tags?: string[];        // 标签
  isPublic?: boolean;
  ownerId?: string;

  // 存储层兼容字段
  _id?: string;
  createdAt?: string;
  updatedAt?: string;
  currentAmount?: number; // Optional hydrated field
}

export interface RecipeIngredient {
  name: string;
  amount: string;
  note?: string;
  linkedIngredientId?: string; // To check stock
}

export interface Recipe {
    id: string;
    name: string;
    time: string;
    difficulty: number; // 1-5 stars
  calories: number;
  manualCalories?: number;
  autoCalories?: number;
  image: string;
  tags: string[];
  description?: string;
    ingredients?: RecipeIngredient[];
    steps?: string[];
    nutrition?: NutritionData;
    difficultyRatingAverage?: number;
    difficultyRatingCount?: number;
    loveRatingAverage?: number;
    loveRatingCount?: number;
    favoriteCount?: number;
    ownerId?: string; // To distinguish user recipes from public ones
    isFavorite?: boolean; // UI state only - persisting via FbUserFavorite table
  }

// =============================================
// 食谱分类体系 (2026-03-10)
// =============================================

/**
 * 食谱标签一级分类
 */
export type RecipeTagCategoryType = 
  | 'cuisine'      // 菜系
  | 'method'       // 做法
  | 'role'         // 角色
  | 'nutrition'    // 营养
  | 'taste'        // 口味
  | 'scene'        // 场景
  | 'difficulty'   // 难度
  | 'crowd'        // 人群
  | 'season'       // 时令
  | 'tool';        // 用具

/**
 * 食谱标签分类项
 */
export interface RecipeTagCategory {
  id: RecipeTagCategoryType;
  label: string;
  icon: string;
  subCategories: string[];
}

/**
 * 食谱标签分类体系常量
 */
export const RECIPE_TAG_TAXONOMY: RecipeTagCategory[] = [
  {
    id: 'cuisine',
    label: '菜系',
    icon: 'restaurant',
    subCategories: ['川菜', '粤菜', '湘菜', '东北菜', '鲁菜', '苏菜', '浙菜', '闽菜', '徽菜', '日本菜', '韩国菜', '泰国菜', '越南菜', '马来西亚菜', '印度菜', '意大利菜', '法国菜', '美式快餐']
  },
  {
    id: 'method',
    label: '做法',
    icon: 'outdoor_grill',
    subCategories: ['炒', '煎', '炸', '熘', '蒸', '煮', '炖', '熬', '焖', '烤', '焗', '拌', '腌', '泡', '熏', '卤']
  },
  {
    id: 'role',
    label: '角色',
    icon: 'lunch_dining',
    subCategories: ['米饭', '面食', '粥', '粉线', '荤菜', '素菜', '汤羹', '小吃', '早点', '烘焙', '甜品', '沙拉', '酒水', '饮料']
  },
  {
    id: 'nutrition',
    label: '营养',
    icon: 'favorite',
    subCategories: ['高蛋白', '高碳水', '高纤维', '低卡路里', '低脂肪', '低钠', '低糖', '无麸质', '素食', '纯素', '生酮']
  },
  {
    id: 'taste',
    label: '口味',
    icon: 'local_fire_department',
    subCategories: ['不辣', '微辣', '中辣', '重辣', '清淡', '咸鲜', '酸甜', '麻辣', '香辣', '酱香', '蒜香', '咖喱']
  },
  {
    id: 'scene',
    label: '场景',
    icon: 'wb_sunny',
    subCategories: ['早餐', '午餐', '晚餐', '下午茶', '夜宵', '便当', '宴客', '一人食']
  },
  {
    id: 'difficulty',
    label: '难度',
    icon: 'speed',
    subCategories: ['快手菜', '家常菜', '精致料理']
  },
  {
    id: 'crowd',
    label: '人群',
    icon: 'groups',
    subCategories: ['儿童餐', '老人餐', '孕妇餐', '健身餐', '减脂餐', '病后调理', '养胃清淡']
  },
  {
    id: 'season',
    label: '时令',
    icon: 'eco',
    subCategories: ['春季菜', '夏季清凉', '秋季滋补', '冬季暖身']
  },
  {
    id: 'tool',
    label: '用具',
    icon: 'kitchen',
    subCategories: ['炒锅', '煎锅', '汤锅', '蒸锅', '砂锅', '高压锅', '电饭锅', '烤箱', '空气炸锅', '微波炉', '电磁炉', '电炖锅', '豆浆机', '破壁机', '电饼铛']
  }
];

/**
 * 获取所有标签的扁平列表
 */
export const getAllRecipeTags = (): string[] => {
  return RECIPE_TAG_TAXONOMY.flatMap(cat => cat.subCategories);
};

/**
 * 热门标签（用于快捷筛选）
 */
export const POPULAR_RECIPE_TAGS = [
  '川菜', '粤菜', '家常菜', '快手菜',
  '炒', '蒸', '炖', '烤',
  '荤菜', '素菜', '汤羹', '甜品',
  '低卡路里', '高蛋白', '低脂',
  '微辣', '麻辣', '清淡',
  '早餐', '晚餐', '便当', '一人食'
];

export interface FbRecipeDb {
  _id: string;
  name: string;
  time?: string;
  difficulty?: number;
  calories?: number;
  image?: string;
  tags?: string; // JSON string
  description?: string;
  steps?: string; // JSON string
  isPublic?: boolean;
  ownerId?: string;
  _openid?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FbRecipeIngredientDb {
  _id: string;
  recipeId: string;
  ingredientId?: string | null;
  name: string;
  amount: string;
  sortOrder: number;
  _openid?: string;
  createdAt?: string;
}

export interface FbScheduleDb {
  _id: string;
  date: string;
  time: string;
  icon?: string;
  status?: string;
  items?: string; // JSON string
  userId: string;
  _openid?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShoppingItem {
  id: string;
  name: string;
  quantity: string;
  checked: boolean;
  date?: string; // YYYY-MM-DD
  linkedIngredientId?: string; // If bought, add to this ingredient stock
  batchData?: Partial<StockBatch>; // For checkout editing
}

/**
 * BMI分类枚举（中国成人标准 WS/T 428-2013）
 */
export type BMICategory = 'underweight' | 'normal' | 'overweight' | 'obese';

/**
 * BMI分类标签映射
 */
export const BMI_CATEGORY_LABELS: Record<BMICategory, string> = {
  underweight: '偏瘦',
  normal: '正常',
  overweight: '超重',
  obese: '肥胖'
};

/**
 * BMI分类颜色映射（用于UI显示）
 */
export const BMI_CATEGORY_COLORS: Record<BMICategory, string> = {
  underweight: 'text-blue-500 bg-blue-100 border-blue-200',
  normal: 'text-green-500 bg-green-100 border-green-200',
  overweight: 'text-orange-500 bg-orange-100 border-orange-200',
  obese: 'text-red-500 bg-red-100 border-red-200'
};

/**
 * 性别类型
 */
export type Gender = 'male' | 'female' | 'unspecified';

/**
 * 性别标签映射
 */
export const GENDER_LABELS: Record<Gender, string> = {
  male: '男',
  female: '女',
  unspecified: '未指定'
};

export interface UserProfile {
  id: string;
  name: string;
  email?: string;
  level: number;
  xp: number;
  maxXp: number;
  theme: 'light' | 'dark';
  joinedDate: string;
  favoriteRecipeIds: string[];
  favoriteIngredientIds: string[];
  goals: {
    height: number; // cm
    weight: number; // kg
    targetCalories: number;
    targetProtein: number;
    targetFat: number;
    targetCarbs: number;
    targetFiber: number;
  };
  allergies: string[];
  dietaryRestrictions: string[];
  // ========== 新增字段 (BMI增强功能) ==========
  /** BMI值，保留1位小数 */
  bmi?: number;
  /** BMI分类 */
  bmiCategory?: BMICategory;
  /** 基础代谢率 (kcal) */
  bmr?: number;
  /** 每日总能量消耗 (kcal) */
  tdee?: number;
  /** 性别 */
  gender?: Gender;
  /** 出生日期 (YYYY-MM-DD) */
  birthdate?: string;
  /** 烹饪用具列表 */
  cookingTools?: string[];
  /** 活动水平 */
  activityLevel?: ActivityLevel;
  /** 目标类型 */
  goalType?: GoalType;
  /** 菜系偏好 */
  cuisinePreferences?: string[];
  /** 口味偏好 */
  tastePreferences?: string[];
  /** 难度偏好 */
  difficultyPreference?: 'quick' | 'home' | 'gourmet';
  recipeRatings?: Record<string, RecipeUserRatingRecord>;
  /** 数据更新时间 */
  updatedAt?: string;
}

export interface RecipeUserRatingValue {
  value: number;
  ratedAt?: string;
}

export interface RecipeUserRatingRecord {
  difficulty?: number | RecipeUserRatingValue;
  love?: number | RecipeUserRatingValue;
}

export type ScheduleByDate = {
  [date: string]: MealTime[];
};

/**
 * 用户自定义时段配置
 */
export interface UserMealTimeConfig {
  id: string;
  title: string;
  time: string;
  icon: string;
  sortOrder: number;
  isDefault: boolean;
  bgClass?: string;
  borderClass?: string;
  titleColorClass?: string;
}

/**
 * 计划统计信息
 */
export interface ScheduleStats {
  dateRange: { start: string; end: string };
  totalMeals: number;
  completedMeals: number;
  completionRate: number;
  totalCalories: number;
  averageDailyCalories: number;
  nutritionTotals: NutritionData;
  dailyStats: {
    date: string;
    totalMeals: number;
    completedMeals: number;
    totalCalories: number;
  }[];
}

/**
 * 批量操作类型
 */
export type BatchOperationType = 'copy' | 'delete' | 'move';

/**
 * 批量操作请求
 */
export interface BatchScheduleOperation {
  type: BatchOperationType;
  sourceDate: string;
  targetDate?: string;
  mealTitles?: string[]; // 如果为空，则操作所有时段
}

/**
 * 购物清单生成选项
 */
export interface ShoppingListGenerateOptions {
  dates: string[]; // 要生成购物清单的日期列表
  excludeInventory?: boolean; // 是否排除已有库存
  mergeSameIngredients?: boolean; // 是否合并相同食材
}

/**
 * Log entry for the user log center
 */
export interface LogEntry {
  id: string;
  userId: string;
  content: string;
  createdAt: string; // ISO 8601 timestamp
  isRead: boolean;
}

/**
 * Ingredient-Recipe relationship index for bidirectional queries
 */
export interface IngredientRecipeIndex {
  [ingredientId: string]: {
    publicRecipes: string[]; // Public recipe IDs that use this ingredient
    userRecipes: Map<string, string[]>; // userId -> user recipe IDs that use this ingredient
  };
}

// =============================================
// 用户档案相关类型定义 (2026-02-02)
// =============================================

/**
 * 用户基本信息
 */
export interface UserBasicInfo {
  id?: string;
  email?: string;
  nickname?: string;
  avatarUrl?: string;
  phone?: string;
  gender?: Gender | 'other';
  birthday?: string; // YYYY-MM-DD
  bio?: string;
  timezone?: string;
  language?: string;
  emailVerified?: boolean;
  createdAt?: string;
  lastLoginAt?: string;
}

/**
 * 活动等级枚举
 */
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';

/**
 * 口味偏好枚举
 */
export type TastePreference = 'sweet' | 'salty' | 'spicy' | 'sour' | 'bitter' | 'umami';

/**
 * 饮食限制枚举
 */
export type DietaryRestriction = 
  | 'vegetarian' 
  | 'vegan' 
  | 'halal' 
  | 'kosher' 
  | 'gluten_free' 
  | 'dairy_free' 
  | 'low_carb' 
  | 'keto' 
  | 'paleo';

/**
 * 目标类型枚举
 */
export type GoalType = 'maintain' | 'lose_weight' | 'gain_weight' | 'muscle_building';

/**
 * 烹饪技能水平
 */
export type CookingSkillLevel = 'beginner' | 'intermediate' | 'advanced';

/**
 * 餐类型枚举
 */
export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';

/**
 * 用户身体数据
 */
export interface UserBodyData {
  height?: number; // cm
  weight?: number; // kg
  targetWeight?: number; // kg
  activityLevel?: ActivityLevel | string;
  bmr?: number; // 基础代谢率
  tdee?: number; // 每日总能量消耗
}

/**
 * 用户饮食偏好
 */
export interface DietaryPreferences {
  tastePreference?: TastePreference[]; // 口味偏好
  cuisinePreference?: string[]; // 菜系偏好
  dislikedIngredients?: string[]; // 不喜欢食材
  allergens?: string[]; // 过敏原
  dietaryRestrictions?: Array<DietaryRestriction | string>; // 饮食限制
  mealFrequency?: number; // 每日就餐次数
  snackPreference?: boolean; // 是否吃零食
  cookingSkillLevel?: CookingSkillLevel; // 烹饪技能水平
}

/**
 * 用户营养目标
 */
export interface NutritionGoals {
  targetCalories?: number;
  targetProtein?: number;
  targetFat?: number;
  targetCarbs?: number;
  targetFiber?: number;
  targetSodium?: number;
  targetSugar?: number;
  goalType?: GoalType | string;
  startDate?: string;
  endDate?: string;
}

/**
 * 收藏类型
 */
export type FavoriteType = 'recipe' | 'ingredient';

/**
 * 用户收藏记录
 */
export interface UserFavorite {
  id: string;
  userId: string;
  type: FavoriteType;
  itemId: string;
  itemName?: string;
  itemImage?: string;
  createdAt: string;
}

/**
 * 用户自定义食谱
 */
export interface UserRecipe {
  id: string;
  userId: string;
  name: string;
  description?: string;
  image?: string;
  time?: string;
  difficulty?: number; // 1-5
  calories?: number;
  manualCalories?: number;
  autoCalories?: number;
  nutrition?: NutritionData;
    tags?: string[];
    steps?: string[];
    ingredients?: RecipeIngredient[];
    difficultyRatingAverage?: number;
    difficultyRatingCount?: number;
    loveRatingAverage?: number;
    loveRatingCount?: number;
    isPublic?: boolean;
    ownerId?: string; // 用于区分公共与用户私有数据
  createdAt?: string;
  updatedAt?: string;
}

/**
 * 用户自定义食材
 */
export interface UserIngredient {
    id: string;
    userId: string;
    name: string;
    aliasNames?: string[];
    englishName?: string;
    tags?: string[];
    category?: string;
    subCategory?: string;
    unit?: string;
  calories?: number;
  nutrition?: NutritionData;
  protein?: number;
  fat?: number;
  carbs?: number;
  fiber?: number;
  nutritionExtended?: Record<string, any>;
  image?: string;
  description?: string;
  isPublic?: boolean;
    ownerId?: string; // 用于区分公共与用户私有数据
  createdAt?: string;
  updatedAt?: string;
}

/**
 * 用户用餐记录
 */
export interface MealLog {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD
  mealType: MealType;
  recipeId?: string;
  ingredientId?: string;
  customName?: string;
  amount?: string;
  nutrition?: NutritionData;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * 用户设置
 */
export interface UserSettings {
  id: string;
  userId: string;
  theme?: 'light' | 'dark';
  notifications?: {
    mealReminder?: boolean;
    expiryWarning?: boolean;
    nutritionReport?: boolean;
  };
  privacy?: {
    showProfile?: boolean;
    showRecipes?: boolean;
  };
  preferences?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * 完整用户档案（用于前端展示）
 */
export interface CompleteUserProfile {
  basicInfo: Partial<UserBasicInfo>;
  bodyData: UserBodyData;
  dietaryPreferences: DietaryPreferences;
  nutritionGoals: NutritionGoals;
  favoriteRecipeIds: string[];
  favoriteIngredientIds: string[];
  customRecipeCount: number;
  customIngredientCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * 用户档案更新请求
 */
export interface UserProfileUpdateRequest {
  basicInfo?: Partial<Omit<UserBasicInfo, 'id' | 'email' | 'createdAt' | 'lastLoginAt'>>;
  bodyData?: UserBodyData;
  dietaryPreferences?: DietaryPreferences;
  nutritionGoals?: NutritionGoals;
}

/**
 * 营养摄入统计
 */
export interface NutritionIntakeStats {
  date: string;
  totalCalories: number;
  totalProtein: number;
  totalFat: number;
  totalCarbs: number;
  totalFiber: number;
  meals: {
    breakfast: NutritionData;
    lunch: NutritionData;
    dinner: NutritionData;
    snack: NutritionData;
  };
  goal: NutritionGoals;
  completion: {
    calories: number;
    protein: number;
    fat: number;
    carbs: number;
    fiber: number;
  };
}

// =============================================
// AI 食谱生成相关类型定义
// =============================================

/**
 * 支持的AI模型类型
 */
export type AIModelType = 'openai' | 'deepseek' | 'hunyuan' | 'custom';

/**
 * AI配置
 */
export interface AIConfig {
  apiKey: string;           // API密钥（加密存储）
  modelType: AIModelType;   // 模型类型
  modelName?: string;       // 具体模型名称（如gpt-4, deepseek-v3等）
  apiEndpoint?: string;     // 自定义API端点（用于custom类型）
  temperature?: number;     // 温度参数（创造性）
  maxTokens?: number;       // 最大token数
  enabled: boolean;         // 是否启用AI功能
}

/**
 * AI生成请求参数
 */
export interface AIGenerateRequest {
  userInventory: UserInventory;           // 用户库存食材
  dietaryPreferences: DietaryPreferences; // 饮食偏好
  nutritionGoals: NutritionGoals;         // 营养目标
  bodyData: UserBodyData;                 // 身体数据
  mealType?: MealType;                    // 餐次类型
  preferredIngredients?: string[];        // 优先使用的食材ID（必须包含）
  excludedIngredients?: string[];         // 排除的食材ID
  difficulty?: number;                    // 期望难度1-5
  timeLimit?: number;                     // 时间限制（分钟）
  prompt?: string;                        // 额外提示词
  // 新增字段
  servings?: number;                      // 份数，默认 1
  goalType?: GoalType;                    // 饮食目标（显式指定，覆盖用户预设）
  allergens?: string[];                   // 忌口（临时覆盖用户预设）
  // ========== 新增字段 (BMI增强功能) ==========
  /** 用户拥有的烹饪用具 */
  cookingTools?: string[];
  /** 菜系偏好 */
  cuisinePreferences?: string[];
  /** 口味偏好 */
  tastePreferences?: string[];
  /** 难度偏好 */
  difficultyPreference?: 'quick' | 'home' | 'gourmet';
}

/**
 * AI生成的食谱结果
 */
export interface AIGeneratedRecipe {
  name: string;
  description: string;
  time: string;
  difficulty: number;
  calories: number;
  nutrition: NutritionData;
  ingredients: {
    name: string;
    amount: string;
    linkedIngredientId?: string; // 关联的食材ID
    isFromInventory: boolean;    // 是否来自用户库存
  }[];
  steps: string[];
  tags: string[];
  tips?: string[];             // 烹饪小贴士
}

/**
 * AI生成状态
 */
export type AIGenerateStatus = 'idle' | 'generating' | 'success' | 'error';

/**
 * AI生成响应
 */
export interface AIGenerateResponse {
  status: AIGenerateStatus;
  recipe?: AIGeneratedRecipe;
  error?: string;
  rawResponse?: string; // 原始响应（用于调试）
}

// =============================================
// AI 单日计划生成相关类型定义
// =============================================

/**
 * 单个时段的营养分配
 */
export interface MealNutritionAllocation {
  mealType: MealType;
  percentage: number;       // 占全天目标的百分比
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
}

/**
 * 单日计划生成请求中每个时段的配置
 */
export interface DailyPlanMealConfig {
  mealType: MealType;
  nutritionTarget: {
    calories: number;
    protein: number;
    fat: number;
    carbs: number;
    fiber: number;
  };
}

/**
 * 单日计划生成请求
 */
export interface AIDailyPlanRequest {
  baseRequest: AIGenerateRequest;   // 基础配置
  meals: DailyPlanMealConfig[];     // 各时段配置
}

/**
 * 单日计划中单个时段的生成结果
 */
export interface DailyPlanMealResult {
  mealType: MealType;
  recipe?: AIGeneratedRecipe;
  error?: string;
}

/**
 * 单日计划生成响应
 */
export interface AIDailyPlanResponse {
  status: AIGenerateStatus;
  meals: DailyPlanMealResult[];
  totalNutrition?: NutritionData;   // 汇总营养
}

/**
 * AI 生成模式
 */
export type AIGenerateMode = 'single' | 'daily';

// =============================================
// 错误类型导出（从 errorHandler.ts）
// =============================================

export {
  AppError,
  StorageError,
  AuthError,
  NetworkError,
  ValidationError,
  BusinessError,
  LogLevel,
} from './utils/errorHandler';

export type {
  ErrorLog,
} from './utils/errorHandler';
